/**
 * AppSync answers `{ data, errors }` with HTTP 200 even when it failed, and resolves a field with
 * no resolver behind it to `null` with no error at all. Every `lib/api/*` function funnels its
 * result through here so a caller either gets a value or an exception — never a "success" that
 * is quietly empty.
 */
export interface AmplifyResult<T> {
    data?: T | null
    errors?: ReadonlyArray<{ message: string }> | null
}

export class AmplifyOperationError extends Error {
    constructor(readonly operation: string, readonly messages: readonly string[]) {
        super(`${operation}: ${messages.join('; ') || 'no data returned'}`)
        this.name = 'AmplifyOperationError'
    }
}

/** For reads where null is a legitimate answer (a `get` miss): throws on errors, passes null through. */
export function throwOnErrors<T>(result: AmplifyResult<T>, operation: string): T | null {
    if (result.errors?.length) { throw new AmplifyOperationError(operation, result.errors.map((e) => e.message)) }
    return result.data ?? null
}

/** For writes and custom operations, where null data is a failed call, never a success. */
export function requireData<T>(result: AmplifyResult<T>, operation: string): NonNullable<T> {
    const data = throwOnErrors(result, operation)
    if (data === null || data === undefined) { throw new AmplifyOperationError(operation, []) }
    return data
}
